import React from 'react';
import { Helmet } from 'react-helmet';
import SeoSocialTags from '../components/SeoSocialTags';
import SolutionChildHero, { SOLUTION_CHILD_HERO_ILLUSTRATIONS } from '../components/SolutionChildHero';
import './SolutionChildPage.css';

const CANONICAL_URL = 'https://circumvent.io/solutions/assisted-remediation';
const PAGE_TITLE = 'Route verified findings to owners | Circumvent';
const PAGE_DESCRIPTION =
  'After validation, verified Cloud and AppSec risk lands in Jira or a PR with verdict and evidence.';
const NEXT_STEPS_BACKGROUND = `${process.env.PUBLIC_URL}/images/backgrounds/gradient-multi-bg.webp`;
const NEXT_STEPS_SECTION_STYLE = {
  '--cta-bg-image': `url(${NEXT_STEPS_BACKGROUND})`,
};
const LAND_WORK_WITH_THE_RIGHT_TEAM_DIAGRAM =
  '/images/features/diagram_land-work-with-the-right-team.svg';
const LAND_WORK_WITH_THE_RIGHT_TEAM_ICON = '/images/features/icon_team.svg';
const FROM_GUIDANCE_TO_CODE_REVIEW_DIAGRAM =
  '/images/features/diagram_from-guidance-to-code-review.svg';
const FROM_GUIDANCE_TO_CODE_REVIEW_ICON = '/images/features/icon_codereview.svg';
const ORCHESTRATION_AND_CLOSURE_DIAGRAM =
  '/images/features/diagram_orchestration-and-closure.svg';
const ORCHESTRATION_AND_CLOSURE_ICON = '/images/features/icon_orchestration.svg';

const RISK_DETERMINATION_TILES = [
  {
    id: 'false-positive',
    title: 'False Positive',
    body:
      'Close findings that are not well founded so triage stays accurate and queues do not fill with noise.',
    iconSrc: '/images/features/icon_False-positive.svg',
  },
  {
    id: 'risk-reduction',
    title: 'Risk Reduction',
    body:
      'Reduce severity to reflect real organizational context, refocusing security engineering attention to highest risks.',
    iconSrc: '/images/features/icon_Risk-reduction.svg',
  },
  {
    id: 'risk-acceptance',
    title: 'Risk Acceptance',
    body:
      'Record acceptance when risk is low or compensating controls exist, so reporting stays honest and auditable.',
    iconSrc: '/images/features/icon_Risk-acceptance.svg',
  },
];

const VERIFIED_RISK_WORKFLOW_GROUPS = [
  {
    id: 'engineering-practice',
    title: 'Land work with the right team',
    iconSrc: LAND_WORK_WITH_THE_RIGHT_TEAM_ICON,
    intro:
      'Connect remediation to how teams already prioritize and ship. Route each Insight so it lands with ownership that matches Applications and Services, not a generic security inbox.',
    points: [
      {
        title: 'Project space',
        body: 'Create tickets in the correct project space.',
      },
      {
        title: 'Channel',
        body: 'Post into the team channel to get attention.',
      },
      {
        title: 'Planning',
        body: 'Surface work for planning cycle or immediate attention.',
      },
    ],
    diagram: {
      src: LAND_WORK_WITH_THE_RIGHT_TEAM_DIAGRAM,
      alt: 'Flow left to right: Produce Insight, Trigger remediation, then Agent-led routing from live scope. Branches find the owning team, project space, and channel for security handoffs. Caption: Work lands where engineers already plan and respond.',
      width: 1048,
      height: 252,
    },
  },
  {
    id: 'guidance-to-review',
    title: 'From guidance to code review',
    iconSrc: FROM_GUIDANCE_TO_CODE_REVIEW_ICON,
    intro:
      'Support engineers with remediation guidance and integrations they already use for implementation and review.',
    points: [
      {
        title: 'Guidance',
        body:
          'Remediation tied to the verified finding so fixes stay proportional and traceable.',
      },
      {
        title: 'Integrate',
        body:
          'Use approved AI coding workflows so changes stay inside existing guardrails.',
      },
      {
        title: 'Review',
        body: "Pull requests that enter the team's normal review queue.",
      },
    ],
    diagram: {
      src: FROM_GUIDANCE_TO_CODE_REVIEW_DIAGRAM,
      alt: 'Flow left to right: Agent handoff, AI coding agent inside your guardrails, then human review. Caption: Shipping stays human-controlled through your existing review and release paths.',
      width: 1048,
      height: 184,
    },
  },
  {
    id: 'orchestration-closure',
    title: 'Orchestration and closure',
    iconSrc: ORCHESTRATION_AND_CLOSURE_ICON,
    intro:
      'Keep remediation state coherent across tools and time. Security stays informed without manually reconciling tickets, chats, and pipelines.',
    points: [
      {
        title: 'Orchestrate',
        body:
          'The agent aligns to dynamic workflow requirements for different Applications.',
      },
      {
        title: 'Status',
        body:
          'Circumvent status and notes stay current with progress, evidence, and outcomes.',
      },
      {
        title: 'Verify',
        body:
          'Final resolution status remains true to what scanners see in production.',
      },
    ],
    diagram: {
      src: ORCHESTRATION_AND_CLOSURE_DIAGRAM,
      alt: 'Orchestration phase: work tracker, build and deploy, and scanners or monitors feed into an Agent-led traceability hub for remediation state and closure. Caption: Circumvent aligns status and notes across tools so security engineers can follow progress, evidence, and outcomes.',
      width: 1048,
      height: 253,
    },
  },
];

const SolutionAssistedRemediationPage = () => (
  <>
    <Helmet>
      <title>{PAGE_TITLE}</title>
      <meta name="description" content={PAGE_DESCRIPTION} />
      <link rel="canonical" href={CANONICAL_URL} />
    </Helmet>
    <SeoSocialTags
      title={PAGE_TITLE}
      description={PAGE_DESCRIPTION}
      url={CANONICAL_URL}
    />

    <main className="page page--light solution-child-page">
      <SolutionChildHero
        headingId="solution-assisted-remediation-hero-heading"
        eyebrow="Assisted Remediation"
        title="Resolve identified security risks"
        lead="Risk can be resolved through closure, severity reduction, or remediation. When you choose to fix verified findings, Circumvent aligns with how your teams track work from intake through to deployment and confirmation."
        illustration={SOLUTION_CHILD_HERO_ILLUSTRATIONS.remediation}
      />

      <section
        className="page-section page-section--white"
        aria-labelledby="solution-assisted-remediation-risk-reduction-heading"
      >
        <div className="page-container page-container--stack">
          <div className="page-section__header">
            <p className="page-section__eyebrow">Risk determination</p>
            <h2 id="solution-assisted-remediation-risk-reduction-heading">
              Risk-based prioritization
            </h2>
            <p className="solution-child-page__section-dek">
              Verify and properly contextualize risk before deciding to remediate. Remediation is now assisted
              by AI, agents or automation, but it still requires humans in the loop before changes are merged
              and deployed. Triage ensures risk is properly assessed and engineering teams review remediation
              fixes that actually matter.
            </p>
          </div>

          <div className="solution-child-page__stakeholders">
            {RISK_DETERMINATION_TILES.map((tile) => (
              <div key={tile.id} className="solution-child-page__stakeholder-card">
                <span className="solution-child-page__stakeholder-icon" aria-hidden="true">
                  <img src={tile.iconSrc} alt="" width={46} height={46} decoding="async" />
                </span>
                <h3 className="solution-child-page__stakeholder-title">{tile.title}</h3>
                <p>{tile.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className="page-section page-section--pixel-noise"
        aria-labelledby="solution-assisted-remediation-flow-heading"
      >
        <div className="page-container page-container--stack">
          <div className="page-section__header">
            <p className="page-section__eyebrow">Assisted remediation</p>
            <h2 id="solution-assisted-remediation-flow-heading">
              Remediate with engineering workflows that get results
            </h2>
            <p className="solution-child-page__section-dek">
              Use agent-assisted workflows that align with how your engineering teams work; identify the right
              owners, get the work scheduled, and ship changes. Resolution status is tracked end to end.
            </p>
          </div>

          {VERIFIED_RISK_WORKFLOW_GROUPS.map((group) => {
            const headingId = `solution-assisted-remediation-workflow-${group.id}-heading`;

            return (
              <article
                key={group.id}
                className="solution-child-page__card solution-child-page__workflow-card"
                aria-labelledby={headingId}
              >
                <div className="solution-child-page__workflow-card-inner">
                  <div className="solution-child-page__workflow-subheader solution-child-page__workflow-subheader--with-icon solution-child-page__workflow-subheader--split">
                    <div className="solution-child-page__workflow-subheader-heading">
                      <span className="solution-child-page__workflow-subheader-icon" aria-hidden="true">
                        <img src={group.iconSrc} alt="" width={84} height={84} decoding="async" />
                      </span>
                      <h3
                        id={headingId}
                        className="solution-child-page__workflow-subheader-title"
                      >
                        {group.title}
                      </h3>
                    </div>
                    <p className="solution-child-page__workflow-subheader-intro">{group.intro}</p>
                  </div>

                  <ol className="solution-child-page__steps solution-child-page__workflow-step-list solution-child-page__workflow-step-list--columns solution-child-page__workflow-step-list--stacked">
                    {group.points.map((point, index) => (
                      <li key={`${group.id}-point-${index}`}>
                        <div className="solution-child-page__step-copy">
                          <span className="solution-child-page__step-title">{point.title}</span>
                          <span className="solution-child-page__step-body">{point.body}</span>
                        </div>
                      </li>
                    ))}
                  </ol>

                  <div className="solution-child-page__workflow-initiation-graph solution-child-page__workflow-initiation-graph--wide">
                    <figure className="solution-child-page__workflow-initiation-figure">
                      <img
                        className="solution-child-page__workflow-initiation-img"
                        src={group.diagram.src}
                        alt={group.diagram.alt}
                        width={group.diagram.width}
                        height={group.diagram.height}
                        loading="lazy"
                        decoding="async"
                      />
                    </figure>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section
        className="page-section page-section--white solution-child-page__cta-section solution-child-page__cta-section--image"
        aria-labelledby="solution-assisted-remediation-cta-heading"
        style={NEXT_STEPS_SECTION_STYLE}
      >
        <div className="page-container page-container--stack">
          <div className="page-section__header" aria-labelledby="solution-assisted-remediation-cta-heading">
            <h2 id="solution-assisted-remediation-cta-heading">Next step</h2>
            <p className="solution-child-page__cta-lead">
              See Circumvent in action on your environment.
            </p>
            <p className="solution-child-page__cta-actions">
              <a href="/#contact" className="btn btn-solid">
                Book a demo
              </a>
            </p>
          </div>
        </div>
      </section>
    </main>
  </>
);

export default SolutionAssistedRemediationPage;
